import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { useTheme } from '@mui/material/styles';
import Collapse from 'components/Collapse';
import { IconButton } from 'components/buttons';
import Link from 'components/Link';
import { useLocale, useTranslation } from 'hooks';
import { Container, DetailedView } from './Detail/style';
import Detail from './Detail';
import {
  CompanyDetails,
  CompanyLabel,
  ExtraDetails,
  Text,
  TextWrapper,
  TitleWrapper,
  DottedHR
} from './style';
import { ButtonWrapper } from 'components/Resume/style';

const atreusBrandsColors = {
  myhead: '#F7950C',
  atreus: '#206AF5',
  atrova: '#F47320'
};

function Company({ className, data, editPath, indexedMeta, language, expandCollapse }) {
  const location = useLocation();
  const {
    countries,
    functions,
    industries,
    ownershipStructures,
    companyTurnoverList,
    employees
  } = indexedMeta;
  const { tr } = useTranslation();
  const locale = useLocale(language);

  const theme = useTheme();

  const companyEditState = useMemo(() => ({ background: location }), [location]);

  const positionEditState = useMemo(() => ({ background: location }), [location]);

  const addPositionState = useMemo(() => ({ background: location }), [location]);

  function getTimeDiff(from, to) {
    if (from === '') return { diff: null, period: '' };
    let date1 = new Date(from);
    let date2 = to ? new Date(to) : new Date();
    return date2.getTime() - date1.getTime();
  }

  function getTimeOfCompanyExp(data) {
    if (!data) return null;
    if (data.experiences.length === 1) return getTimeOfExp(data.experiences[0]);

    let totalMonthsDiff = data.experiences.reduce((totalDiff, experience) => {
      let diff = getTimeOfExp(experience);
      return totalDiff + (diff.years * 12 + diff.months);
    }, 0);

    const [years, months] = [Math.floor(totalMonthsDiff / 12), totalMonthsDiff % 12];
    const yearsText = years <= 1 ? tr('label.year') : tr('label.years');
    const monthsText = months <= 1 ? tr('label.month') : tr('label.months');
    const yearToShow = years > 0 ? `${years} ${yearsText} ` : '';
    const monthToShow = months > 0 ? `${months} ${monthsText}` : '';

    return { years, months, text: (yearToShow + monthToShow).trim() };
  }

  function getTimeOfExp(experience) {
    if (!experience || !experience.dateFrom) {
      return { years: 0, months: 0, text: '-' };
    }
    let diffDate = new Date(getTimeDiff(experience.dateFrom, experience.dateTo));
    const totalMonthsDiff =
      (diffDate.getUTCFullYear() - 1970) * 12 +
      diffDate.getUTCMonth() +
      (diffDate.getDate() >= 15 ? 1 : 0);
    const [years, months] = [Math.floor(totalMonthsDiff / 12), totalMonthsDiff % 12];
    const yearsText = years <= 1 ? tr('label.year') : tr('label.years');
    const monthsText = months <= 1 ? tr('label.month') : tr('label.months');
    const yearToShow = years > 0 ? `${years} ${yearsText} ` : '';
    const monthToShow = months > 0 ? `${months} ${monthsText}` : '';
    return { years, months, text: (yearToShow + monthToShow).trim() };
  }

  const companyData = useMemo(() => {
    if (!data) return null;
    let companyData = { ...data };
    companyData.timeInfo = {
      to: companyData.isActualEngagement
        ? tr('label.present')
        : companyData.dateTo
        ? format(new Date(companyData.dateTo), 'LLL yyyy', { locale })
        : '-',
      from: companyData.dateFrom
        ? format(new Date(companyData.dateFrom), 'LLL yyyy', { locale })
        : '-',
      period: getTimeOfCompanyExp(companyData).text
    };
    companyData.experiences = companyData.experiences.map(experience => ({
      ...experience,
      timeInfo: {
        to: experience.isActualEngagement
          ? tr('label.present')
          : experience.dateTo
          ? format(new Date(experience.dateTo), 'LLL yyyy', { locale })
          : '-',
        from: experience.dateFrom
          ? format(new Date(experience.dateFrom), 'LLL yyyy', { locale })
          : '-',
        period: getTimeOfExp(experience).text
      }
    }));
    return companyData;
  }, [data, locale, tr]);

  function contractTypeMapping(type) {
    let typeMap = {
      100: 'permanent',
      200: 'interim',
      300: 'consulting',
      400: 'selfEmployed',
      500: 'board',
      600: 'careerBreak'
    };
    return typeMap[type];
  }

  // Sorting logic
  companyData.experiences?.sort((a, b) => {
    const aOngoing = a.dateTo === '';
    const bOngoing = b.dateTo === '';

    if (aOngoing && bOngoing) return 0;
    if (aOngoing) return -1;
    if (bOngoing) return 1;
    return new Date(b.dateTo) - new Date(a.dateTo);
  });

  companyData.experiences?.sort((a, b) => {
    if (a.dateTo === b.dateTo) return new Date(b.dateFrom) - new Date(a.dateFrom);
    return 0;
  });

  companyData.experiences?.sort((a, b) => {
    if (a.dateTo === '' && new Date(a.dateFrom) > new Date(b.dateFrom)) return -1;
    if (b.dateTo === '' && new Date(a.dateFrom) < new Date(b.dateFrom)) return 1;
    return 0;
  });

  return (
    <Container className={className}>
      <div className="clt">
        <TextWrapper>
          <div>
            <Text as="div" className="comp_name">
              <CompanyDetails size="24px" weight={300}>
                {companyData.timeInfo.from} - {companyData.timeInfo.to} {' | '}
                {companyData.companyName}
              </CompanyDetails>
              {editPath && (
                <IconButton
                  className="comp_edit_btn"
                  component={Link}
                  icon="Edit"
                  to={`company/${companyData.experiences?.[0]?.id}`}
                  state={companyEditState}
                />
              )}
            </Text>
            <Text as="div">
              <ExtraDetails>
                <b>{`${tr('label.cityCountry')}: `}</b>
                {companyData.city?.[language] || '-'}
                {companyData.companyCountry ? `, ${countries[companyData.companyCountry]}` : ', -'}
                {` | `}
                <b>{`${tr('label.turnoverInMillion')}: `}</b>
                {companyData.companyTurnover
                  ? companyTurnoverList[companyData.companyTurnover]
                  : '-'}
                {` | `}
                <b>{`${tr('label.numberOfEmployees')}: `}</b>
                {companyData.companyEmployees ? employees[companyData.companyEmployees] : '-'}
                {` | `}
                <b>{`${tr('label.ownershipStructure')}: `}</b>
                {companyData.ownershipStructure
                  ? ownershipStructures[companyData.ownershipStructure]
                  : '-'}
                {` | `}
                <b>{`${tr('label.industry')}: `}</b>
                {companyData.industryDescription?.[language] ||
                  (companyData.industry && companyData.industry !== '10'
                    ? industries[companyData.industry]
                    : '-')}
              </ExtraDetails>
            </Text>
          </div>
        </TextWrapper>

        <ul className="list">
          {companyData.experiences.map(exp => (
            <li className="listItem" key={exp.id}>
              <DetailedView>
                <TitleWrapper>
                  <div>
                    <CompanyDetails className="compPost-title" bold size="14px" weight={700}>
                      {exp.timeInfo.from} - {exp.timeInfo.to}{' '}
                      {exp.timeInfo.period && `(${exp.timeInfo.period})`}{' '}
                      {exp.name?.[language]
                        ? ' | ' + exp.name[language]
                        : functions[exp.function] || '-'}
                    </CompanyDetails>
                    {editPath && (
                      <IconButton
                        className="compPost-btn"
                        component={Link}
                        icon="Edit"
                        to={`${editPath}/${exp.id}`}
                        state={positionEditState}
                      />
                    )}
                  </div>
                </TitleWrapper>
              </DetailedView>
              <DottedHR className="compDivider" />
              <Text as="div">
                <div>
                  {exp.contractType && (
                    <CompanyLabel className="position-Tag" black>
                      {tr('label.' + contractTypeMapping(exp.contractType))}
                    </CompanyLabel>
                  )}
                  {exp.commitment && (
                    <CompanyLabel
                      className="position-Tag"
                      color={
                        atreusBrandsColors[exp.commitment.toLowerCase()] ||
                        theme.palette.primary.main
                      }
                    >
                      {exp.commitment}
                    </CompanyLabel>
                  )}
                </div>
                <ExtraDetails className="textClass">
                  <b>{`${tr('label.posLocation')}: `}</b>
                  {exp.placeOfOccupation?.[language] || '-'}
                  {exp.countryOfOccupation ? `, ${countries[exp.countryOfOccupation]}` : ', -'}
                </ExtraDetails>
                <Collapse
                  togglerPosition="top"
                  expandedProp={expandCollapse}
                  visible={0}
                >
                  <Detail data={exp} indexedMeta={indexedMeta} language={language} />
                </Collapse>
              </Text>
            </li>
          ))}

          {companyData.experiences?.[0]?.companyId && (
            <ButtonWrapper className="listItem">
              <IconButton
                component={Link}
                icon="Plus"
                to={`position-in-company/${companyData.experiences[0].companyId}`}
                state={addPositionState}
              />
              <div className="compAddPosition">
                <Link
                  to={`position-in-company/${companyData.experiences[0].companyId}`}
                  state={addPositionState}
                >
                  <span>{tr('label.addPositionWithinCompany')}</span>
                </Link>
              </div>
            </ButtonWrapper>
          )}
        </ul>
        <DottedHR className="compExp-divider" />
      </div>
    </Container>
  );
}

Company.propTypes = {
  className: PropTypes.string,
  data: PropTypes.object.isRequired,
  editPath: PropTypes.string,
  indexedMeta: PropTypes.shape({
    countries: PropTypes.object.isRequired,
    functions: PropTypes.object.isRequired,
    industries: PropTypes.object.isRequired
  }).isRequired,
  language: PropTypes.string.isRequired,
  expandCollapse: PropTypes.bool
};

export default Company;
